import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PAY } from "../constants/testIds2";
import { PayrollUploadPreview } from "../components/PayrollUploadPreview";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Play, CheckCircle, Trash, FileText, Coins, PencilSimple, Plus, PaperPlaneTilt, UploadSimple, DownloadSimple } from "@phosphor-icons/react";

const RUN_STATUS = {
  draft: "bg-amber-50 text-amber-800 border-amber-200",
  finalized: "bg-blue-50 text-blue-800 border-blue-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

function fmtMoney(n, cur = "INR") {
  if (n == null) return "—";
  return `${cur} ${n.toLocaleString("en-IN")}`;
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Payroll() {
  const { user, tenantId } = useAuth();
  const [runs, setRuns] = useState([]);
  const [structures, setStructures] = useState([]);
  const [editingStructure, setEditingStructure] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [period, setPeriod] = useState(currentMonth());
  const [openRun, setOpenRun] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [openStructure, setOpenStructure] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [structForm, setStructForm] = useState({
    employee_id: "", effective_from: "2026-01-01", currency: "INR",
    monthly_ctc: 0, basic: 0, hra: 0, special_allowance: 0, conveyance: 1600, medical_allowance: 1250,
    employer_pf: 0, employee_pf: 0, esic_employer: 0, esic_employee: 0,
    professional_tax: 200, tds_percent: 0, other_deductions: 0,
    custom_earnings: {}, custom_deductions: {},
  });

  const load = () => {
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/payroll/runs", { params }).then(({ data }) => setRuns(data));
    api.get("/employees", { params }).then(({ data }) => setEmployees(data));
    api.get("/salary-structures", { params }).then(({ data }) => setStructures(data)).catch(() => setStructures([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const createRun = async () => {
    try {
      const payload = { period_month: period };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      const { data } = await api.post("/payroll/runs", payload);
      toast.success(`Generated ${data.generated} payslips for ${period}`);
      setOpenCreate(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPayslips = async (run) => {
    setOpenRun(run);
    const { data } = await api.get(`/payroll/runs/${run.id}/payslips`);
    setPayslips(data);
  };

  const finalize = async (id) => {
    try { await api.patch(`/payroll/runs/${id}`, { action: "finalize" }); toast.success("Run finalized"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const markPaid = async (id) => {
    try { await api.patch(`/payroll/runs/${id}`, { action: "mark_paid" }); toast.success("Marked as paid"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const removeRun = async (r) => {
    const final = r.status !== "draft";
    const msg = final
      ? `This run (${r.period_month}) is ${r.status}. Deleting it removes all its payslips permanently. Type DELETE to confirm.`
      : "Delete this draft payroll run?";
    if (final) { if (window.prompt(msg) !== "DELETE") return; } else if (!window.confirm(msg)) return;
    try { await api.delete(`/payroll/runs/${r.id}`, { params: final ? { force: true } : {} }); toast.success("Payroll run deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null); // run id or "new"
  const downloadPayrollTemplate = async () => {
    const { data } = await api.get("/payroll/import-template", { params: { period_month: period, ...(user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {}) } });
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([data.content], { type: "text/csv" })); a.download = data.filename; a.click();
    if (data.employees !== undefined) toast.success(`Template pre-filled for ${data.employees} employee(s) — attendance, lost days and pro-rated salary included`);
  };
  const startUpload = (target) => { setUploadTarget(target); setTimeout(() => uploadRef.current?.click(), 0); };
  const [preview, setPreview] = useState(null); // { data, file, target }
  const [committing, setCommitting] = useState(false);
  const tenantParams = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
  const doUpload = async (file) => {
    if (!file || !uploadTarget) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const params = uploadTarget === "new" ? { period_month: period, ...tenantParams } : { run_id: uploadTarget };
      const { data } = await api.post("/payroll/upload-preview", fd, { params, headers: { "Content-Type": "multipart/form-data" } });
      setPreview({ data, file, target: uploadTarget });
    } catch (e) { toast.error(formatApiError(e)); }
    if (uploadRef.current) uploadRef.current.value = "";
    setUploadTarget(null);
  };
  const commitUpload = async () => {
    if (!preview) return;
    setCommitting(true);
    const fd = new FormData(); fd.append("file", preview.file);
    try {
      const url = preview.target === "new" ? "/payroll/runs/upload" : `/payroll/runs/${preview.target}/upload`;
      const params = preview.target === "new" ? { period_month: period, ...tenantParams } : {};
      const { data } = await api.post(url, fd, { params, headers: { "Content-Type": "multipart/form-data" } });
      const msg = `Imported ${data.created + data.updated} payslip(s) (${data.created} new, ${data.updated} updated)` + (data.errors.length ? ` · ${data.errors.length} row(s) skipped: ${data.errors[0]}` : "");
      data.errors.length ? toast.warning(msg) : toast.success(msg);
      setPreview(null); setOpenCreate(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setCommitting(false);
  };

  const [emailing, setEmailing] = useState(false);
  const emailPayslips = async () => {
    if (!openRun || !window.confirm(`Email ${payslips.length} payslip(s) for ${openRun.period_month} to employees?`)) return;
    setEmailing(true);
    try {
      const { data } = await api.post(`/payroll/runs/${openRun.id}/email-payslips`);
      const msg = `Sent ${data.sent} · failed ${data.failed} · no email ${data.skipped_no_email}`;
      data.failed ? toast.error(msg) : toast.success(msg);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setEmailing(false);
  };

  const submitStructure = async () => {
    try {
      const payload = { ...structForm };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      Object.keys(payload).forEach((k) => { if (typeof payload[k] === "string" && !isNaN(Number(payload[k])) && k !== "employee_id" && k !== "effective_from" && k !== "currency") payload[k] = Number(payload[k]); });
      if (editingStructure) {
        // Only patch mutable fields (exclude employee_id, tenant_id)
        const { employee_id: _e, tenant_id: _t, id: _i, ...patch } = payload;
        await api.patch(`/salary-structures/${editingStructure}`, patch);
        toast.success("Salary structure updated");
      } else {
        await api.post("/salary-structures", payload);
        toast.success("Salary structure saved");
      }
      setOpenStructure(false); setEditingStructure(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEditStructure = (row) => {
    setEditingStructure(row.id);
    setStructForm({
      employee_id: row.employee_id || "", effective_from: row.effective_from || "2026-01-01", currency: row.currency || "INR",
      monthly_ctc: row.monthly_ctc || 0, basic: row.basic || 0, hra: row.hra || 0, special_allowance: row.special_allowance || 0,
      conveyance: row.conveyance || 0, medical_allowance: row.medical_allowance || 0,
      employer_pf: row.employer_pf || 0, employee_pf: row.employee_pf || 0, esic_employer: row.esic_employer || 0, esic_employee: row.esic_employee || 0,
      professional_tax: row.professional_tax || 0, tds_percent: row.tds_percent || 0, other_deductions: row.other_deductions || 0,
      custom_earnings: row.custom_earnings || {}, custom_deductions: row.custom_deductions || {},
    });
    setOpenStructure(true);
  };

  const openNewStructure = () => {
    setEditingStructure(null);
    setStructForm({
      employee_id: "", effective_from: "2026-01-01", currency: "INR",
      monthly_ctc: 0, basic: 0, hra: 0, special_allowance: 0, conveyance: 1600, medical_allowance: 1250,
      employer_pf: 0, employee_pf: 0, esic_employer: 0, esic_employee: 0,
      professional_tax: 200, tds_percent: 0, other_deductions: 0,
      custom_earnings: {}, custom_deductions: {},
    });
    setOpenStructure(true);
  };

  const removeStructure = async (id) => {
    if (!window.confirm("Delete this salary structure?")) return;
    try { await api.delete(`/salary-structures/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const empName = (eid) => {
    const e = employees.find((x) => x.id === eid);
    return e ? `${e.first_name} ${e.last_name} · ${e.employee_code}` : eid;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Payroll</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Payroll runs</h1>
          <p className="text-sm text-zinc-500 mt-2">Monthly payroll with statutory (PF · ESIC · PT · TDS).</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openStructure} onOpenChange={(v) => { setOpenStructure(v); if (!v) setEditingStructure(null); }}>
            <DialogTrigger asChild>
              <Button variant="outline" onClick={openNewStructure} className="rounded-sm" data-testid="salary-structure-open"><Coins size={16} className="mr-1.5" /> Set salary structure</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid={PAY.structureForm}>
              <DialogHeader><DialogTitle className="font-display text-2xl">{editingStructure ? "Edit salary structure" : "Add / update salary structure"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs">Employee</Label>
                  <Select value={structForm.employee_id} onValueChange={(v) => setStructForm({ ...structForm, employee_id: v })} disabled={!!editingStructure}>
                    <SelectTrigger className="rounded-sm" data-testid="structure-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                  </Select>
                  {editingStructure && <div className="text-[10px] text-zinc-500">Employee cannot be changed on edit. Delete &amp; recreate if reassigning.</div>}
                </div>
                <div className="space-y-1"><Label className="text-xs">Effective from</Label><Input type="date" value={structForm.effective_from} onChange={(e) => setStructForm({ ...structForm, effective_from: e.target.value })} className="rounded-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Currency</Label><Input value={structForm.currency} onChange={(e) => setStructForm({ ...structForm, currency: e.target.value.toUpperCase() })} className="rounded-sm font-mono-plex" /></div>
                <div className="space-y-1"><Label className="text-xs">Monthly CTC</Label><Input type="number" value={structForm.monthly_ctc} onChange={(e) => setStructForm({ ...structForm, monthly_ctc: e.target.value })} className="rounded-sm font-mono-plex" /></div>
                {[
                  ["basic", "Basic"], ["hra", "HRA"], ["special_allowance", "Special allowance"], ["conveyance", "Conveyance"], ["medical_allowance", "Medical"],
                  ["employer_pf", "Employer PF"], ["employee_pf", "Employee PF"], ["esic_employer", "Employer ESIC"], ["esic_employee", "Employee ESIC"],
                  ["professional_tax", "Professional Tax"], ["tds_percent", "TDS %"], ["other_deductions", "Other deductions"],
                ].map(([k, l]) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-xs">{l}</Label>
                    <Input type="number" value={structForm[k]} onChange={(e) => setStructForm({ ...structForm, [k]: e.target.value })} className="rounded-sm font-mono-plex" data-testid={`structure-${k}-input`} />
                  </div>
                ))}

                <CustomComponents
                  title="Additional earnings"
                  keyName="custom_earnings"
                  hint="LTA, Internet allowance, Fuel, Special bonus, etc."
                  structForm={structForm}
                  setStructForm={setStructForm}
                />
                <CustomComponents
                  title="Additional deductions"
                  keyName="custom_deductions"
                  hint="Loan EMI, Advance recovery, Insurance premium, etc."
                  structForm={structForm}
                  setStructForm={setStructForm}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setOpenStructure(false); setEditingStructure(null); }} className="rounded-sm">Cancel</Button>
                <Button onClick={submitStructure} className="btn-zenith rounded-sm" data-testid={PAY.structureSaveBtn}>{editingStructure ? "Save changes" : "Save structure"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <input ref={uploadRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="payroll-upload-input" />
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={PAY.createRunBtn}><Play size={16} className="mr-1.5" /> Run payroll</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Run payroll for a month</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <Label className="text-xs">Period (YYYY-MM)</Label>
                <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-sm" data-testid="payroll-period-input" />
                <div className="text-xs text-zinc-500">Generates one payslip per active employee with a salary structure, adjusted for attendance-based present days.</div>
                <div className="border-t border-zinc-200 pt-3 mt-3 space-y-2">
                  <div className="text-xs font-semibold text-zinc-700">Or upload payroll data manually</div>
                  <div className="text-[11px] text-zinc-500">The template for {period} is pre-filled with <b>every active employee</b> — present / working / lost days from attendance and salary pro-rated for lost days. Edit any amount, then upload — you'll review parsed rows, lost-day deductions and totals before anything is committed. Columns: employee_code, present_days, working_days, basic, hra, special_allowance, conveyance, medical_allowance, other_earnings, employee_pf, employer_pf, employee_esic, employer_esic, professional_tax, tds, other_deductions.</div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={downloadPayrollTemplate} className="rounded-sm" data-testid="payroll-template-btn"><DownloadSimple size={12} className="mr-1" /> Template</Button>
                    <Button variant="outline" size="sm" onClick={() => startUpload("new")} className="rounded-sm" data-testid="payroll-upload-new-btn"><UploadSimple size={12} className="mr-1" /> Upload file for {period}</Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenCreate(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={createRun} className="btn-zenith rounded-sm" data-testid="payroll-create-confirm">Generate payslips</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-x-auto bg-white" data-testid="structures-list">
        <div className="px-4 py-2 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
          <div className="font-display font-bold text-sm">Salary structures <span className="text-xs font-normal text-zinc-500 ml-1">({structures.length})</span></div>
          <div className="text-[10px] text-zinc-500">Click <PencilSimple size={10} className="inline" /> to edit an existing structure.</div>
        </div>
        <table className="hr-table">
          <thead><tr><th>Employee</th><th>Effective from</th><th>CTC (mo)</th><th>Basic</th><th>HRA</th><th>Special</th><th>PT</th><th>TDS%</th><th></th></tr></thead>
          <tbody>
            {structures.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-zinc-500 text-sm">No salary structures yet. Add one to include employees in payroll.</td></tr>}
            {structures.map((r) => (
              <tr key={r.id} data-testid={`structure-row-${r.id}`}>
                <td>{empName(r.employee_id)}</td>
                <td className="font-mono-plex text-xs">{r.effective_from}</td>
                <td className="font-mono-plex text-xs">{fmtMoney(r.monthly_ctc, r.currency)}</td>
                <td className="font-mono-plex text-xs">{fmtMoney(r.basic, r.currency)}</td>
                <td className="font-mono-plex text-xs">{fmtMoney(r.hra, r.currency)}</td>
                <td className="font-mono-plex text-xs">{fmtMoney(r.special_allowance, r.currency)}</td>
                <td className="font-mono-plex text-xs">{fmtMoney(r.professional_tax, r.currency)}</td>
                <td className="font-mono-plex text-xs">{r.tds_percent || 0}%</td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => openEditStructure(r)} className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 rounded-sm" data-testid={`structure-edit-${r.id}`}><PencilSimple size={12} className="inline" /></button>
                    <button onClick={() => removeStructure(r.id)} className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm" data-testid={`structure-del-${r.id}`}><Trash size={12} className="inline" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="overflow-x-auto border border-zinc-200 rounded-sm" data-testid={PAY.runsList}>
        <table className="hr-table">
          <thead><tr><th>Period</th><th>Status</th><th>Payslips</th><th>Total gross</th><th>Total net</th><th>Total CTC</th><th>Created</th><th>Actions</th></tr></thead>
          <tbody>
            {runs.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-zinc-500 text-sm">No payroll runs yet.</td></tr>}
            {runs.map((r) => (
              <tr key={r.id}>
                <td className="font-mono-plex text-sm font-semibold">{r.period_month}</td>
                <td><Badge variant="outline" className={`rounded-sm ${RUN_STATUS[r.status] || ""}`}>{r.status}</Badge></td>
                <td className="font-mono-plex text-xs">{r.payslip_count}</td>
                <td className="font-mono-plex text-xs">{fmtMoney(Math.round(r.total_gross))}</td>
                <td className="font-mono-plex text-xs">{fmtMoney(Math.round(r.total_net))}</td>
                <td className="font-mono-plex text-xs">{fmtMoney(Math.round(r.total_ctc))}</td>
                <td className="font-mono-plex text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    <button onClick={() => openPayslips(r)} className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 rounded-sm" data-testid={`payroll-view-${r.id}`}><FileText size={12} className="inline mr-1" /> View</button>
                    {r.status === "draft" && <button onClick={() => finalize(r.id)} className="text-xs px-2 py-1 border border-blue-300 text-blue-800 hover:bg-blue-50 rounded-sm" data-testid={PAY.finalizeBtn(r.id)}><CheckCircle size={12} className="inline mr-1" /> Finalize</button>}
                    {r.status === "finalized" && <button onClick={() => markPaid(r.id)} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm" data-testid={`payroll-paid-${r.id}`}>Mark paid</button>}
                    {r.status === "draft" && <button onClick={() => startUpload(r.id)} className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 rounded-sm" data-testid={`payroll-upload-${r.id}`}><UploadSimple size={12} className="inline mr-1" /> Upload data</button>}
                    <button onClick={() => removeRun(r)} className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm" data-testid={`payroll-delete-${r.id}`}><Trash size={12} className="inline" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PayrollUploadPreview preview={preview?.data} fileName={preview?.file?.name} busy={committing} onCancel={() => setPreview(null)} onConfirm={commitUpload} />

      {/* Payslips modal */}
      <Dialog open={!!openRun} onOpenChange={(v) => !v && setOpenRun(null)}>
        <DialogContent className="max-w-4xl" data-testid={PAY.runDetail}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Payslips · {openRun?.period_month}</DialogTitle>
            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="text-xs text-zinc-500">
                {openRun?.payslips_emailed_at ? `Last emailed ${new Date(openRun.payslips_emailed_at).toLocaleString()} · sent ${openRun.payslips_email_stats?.sent ?? 0}, failed ${openRun.payslips_email_stats?.failed ?? 0}` : "Payslips have not been emailed yet"}
              </div>
              <Button size="sm" onClick={emailPayslips} disabled={emailing || !payslips.length || openRun?.status === "draft"} className="btn-zenith rounded-sm" title={openRun?.status === "draft" ? "Finalize the run first" : ""} data-testid="payroll-email-payslips">
                <PaperPlaneTilt size={14} className="mr-1" /> {emailing ? "Sending…" : "Email payslips"}
              </Button>
            </div>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto">
            <table className="hr-table">
              <thead><tr><th>Employee</th><th>Present / Working</th><th>Gross</th><th>Deductions</th><th>Net pay</th><th>CTC</th></tr></thead>
              <tbody>
                {payslips.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="text-sm">{p.employee_name}</div>
                      <div className="text-xs font-mono-plex text-zinc-500">{p.employee_code}</div>
                    </td>
                    <td className="font-mono-plex text-xs">{p.present_days} / {p.working_days}</td>
                    <td className="font-mono-plex text-xs">{fmtMoney(p.gross, p.structure_snapshot?.currency)}</td>
                    <td className="font-mono-plex text-xs">{fmtMoney(p.deductions?.total, p.structure_snapshot?.currency)}</td>
                    <td className="font-mono-plex text-xs font-semibold text-emerald-700">{fmtMoney(p.net_pay, p.structure_snapshot?.currency)}</td>
                    <td className="font-mono-plex text-xs">{fmtMoney(p.cost_to_company, p.structure_snapshot?.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CustomComponents({ title, keyName, hint, structForm, setStructForm }) {
  const items = structForm[keyName] || {};
  const entries = Object.entries(items);
  const add = () => {
    let label = window.prompt(`New ${title.toLowerCase()} component name:`);
    if (!label) return;
    label = label.trim();
    if (!label) return;
    if (items[label] != null) { toast.error("Component already exists"); return; }
    setStructForm({ ...structForm, [keyName]: { ...items, [label]: 0 } });
  };
  const update = (label, value) => setStructForm({ ...structForm, [keyName]: { ...items, [label]: value } });
  const remove = (label) => {
    const { [label]: _drop, ...rest } = items;
    setStructForm({ ...structForm, [keyName]: rest });
  };
  const testKind = keyName === "custom_earnings" ? "earn" : "ded";
  return (
    <div className="col-span-3 border-t border-zinc-200 pt-3 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-semibold text-adb-navy">{title}</div>
          <div className="text-[10px] text-zinc-500">{hint}</div>
        </div>
        <Button size="sm" variant="outline" onClick={add} className="rounded-sm h-7" data-testid={`custom-${testKind}-add`}>
          <Plus size={12} className="mr-1" /> Add component
        </Button>
      </div>
      {entries.length === 0 ? (
        <div className="text-[10px] text-zinc-400 italic">No custom components added.</div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {entries.map(([label, val]) => (
            <div key={label} className="flex items-end gap-1">
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] text-zinc-600">{label}</Label>
                <Input type="number" value={val} onChange={(e) => update(label, parseFloat(e.target.value) || 0)} className="rounded-sm font-mono-plex h-8" data-testid={`custom-${testKind}-input-${label}`} />
              </div>
              <button onClick={() => remove(label)} className="w-8 h-8 grid place-items-center text-red-600 border border-red-200 rounded-sm hover:bg-red-50" data-testid={`custom-${testKind}-del-${label}`}><Trash size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
